<script>
const stack = [];
</script>

<script setup>
import { watch, onBeforeUnmount, ref, nextTick } from 'vue';
import Icon from './Icon.vue';

const props = defineProps({
  open: { type: Boolean, default: false },
  title: { type: String, default: '' },
  subtitle: { type: String, default: '' },
  width: { type: Number, default: 560 }
});

const emit = defineEmits(['close']);
const panel = ref(null);
const token = Symbol('modal');

function onKey(e) {
  if (e.key === 'Escape' && stack[stack.length - 1] === token) {
    e.stopPropagation();
    emit('close');
  }
}

function activate() {
  stack.push(token);
  window.addEventListener('keydown', onKey);
  nextTick(() => {
    const target = panel.value?.querySelector('[autofocus]') || panel.value;
    target?.focus({ preventScroll: true });
  });
}

function deactivate() {
  const i = stack.indexOf(token);
  if (i !== -1) stack.splice(i, 1);
  window.removeEventListener('keydown', onKey);
}

watch(
  () => props.open,
  (open) => (open ? activate() : deactivate()),
  { immediate: true }
);

onBeforeUnmount(deactivate);

let downOnOverlay = false;
const onOverlayDown = () => {
  downOnOverlay = true;
};
const onOverlayUp = () => {
  if (downOnOverlay) emit('close');
  downOnOverlay = false;
};
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="open"
        class="overlay"
        @pointerdown.self="onOverlayDown"
        @pointerup.self="onOverlayUp"
      >
        <div
          ref="panel"
          class="panel"
          role="dialog"
          aria-modal="true"
          :aria-label="title"
          tabindex="-1"
          :style="{ width: `${width}px` }"
        >
          <header v-if="title" class="panel-head">
            <div>
              <h2 class="panel-title">{{ title }}</h2>
              <p v-if="subtitle" class="panel-sub">{{ subtitle }}</p>
            </div>
            <button class="close" type="button" aria-label="Close" @click="emit('close')">
              <Icon name="close" :size="18" />
            </button>
          </header>
          <slot name="tabs" />
          <div class="panel-body">
            <slot />
          </div>
          <footer v-if="$slots.footer" class="panel-foot">
            <slot name="footer" />
          </footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
.overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(9, 3, 21, 0.48);
  backdrop-filter: blur(3px);
}

.panel {
  max-width: 100%;
  max-height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
  background: var(--surface);
  border-radius: var(--radius-l);
  box-shadow: var(--shadow-pop);
  outline: none;
  overflow: hidden;
}

.panel-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 22px 24px 0;
}

.panel-title {
  font-size: 19px;
  font-weight: 600;
  line-height: 1.3;
}

.panel-sub {
  margin-top: 3px;
  font-size: 13px;
  color: var(--text-3);
}

.close {
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  margin: -4px -8px 0 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--text-3);
  cursor: pointer;

  &:hover {
    background: var(--bg);
    color: var(--text);
  }
}

.panel-body {
  padding: 20px 24px 24px;
  overflow-y: auto;
}

.panel-foot {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 24px;
  border-top: 1px solid var(--line);
  background: var(--surface-2);
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.18s ease;

  .panel {
    transition: transform 0.18s ease;
  }
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;

  .panel {
    transform: translateY(8px) scale(0.985);
  }
}
</style>
